import authService from '@/services/auth.service';
import Vue from 'vue';
import VueRouter from 'vue-router';
import { AUTHENTICATION_REFRESH_TOKEN } from '@/store/types/action-types';
import { USER_TYPE, REDIRECT_TO_HOME_WITH_LOGGED_USER_PAGES } from '@/constants/common.constants';
import {
  HOME_PAGE_URL,
  ADVISER_HOME_PAGE_URL,
  PAGE_NOT_FOUND_PAGE_URL,
  LOGIN_PAGE_URL,
} from '@/constants/page.constants';
import store from '@/store';
import isEmpty from 'lodash-es/isEmpty';
import indexOf from 'lodash-es/indexOf';
import { adobeTargetTriggerView } from '@/common/utils';

Vue.use(VueRouter);

const routes = [
  {
    path: '/home',
    name: 'Home',
    meta: { requiresAuth: true, title: 'Paymentshield | Home' },
    component: () => import('@/views/Home/HomeView.vue'),
  },
  {
    path: '/login',
    name: 'Login',
    meta: { title: 'Paymentshield | Login', hasBackgroundImg: true },
    component: () => import('@/views/Login/LoginView.vue'),
  },
  {
    path: '/register',
    name: 'Register',
    meta: { title: 'Paymentshield | Register' },
    component: () => import('@/views/Registration/RegistrationView.vue'),
  },
  {
    path: '/portal-register',
    name: 'PortalRegister',
    meta: { title: 'Paymentshield | Register' },
    component: () => import('@/views/Registration/PortalRegistrationView.vue'),
  },
  {
    path: '/register/active',
    name: 'ActiveAccount',
    meta: { title: 'Paymentshield | Active Account' },
    component: () => import('@/views/Registration/ActiveAccountView.vue'),
  },
  {
    path: '/register-success',
    name: 'RegisterSuccess',
    meta: { title: 'Paymentshield | Register Success' },
    component: () => import('@/views/Registration/RegisterSuccessView.vue'),
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    meta: { title: 'Paymentshield | Forgot Password' },
    component: () => import('@/views/ForgotPassword/ForgotPasswordView.vue'),
  },
  {
    path: '/forgot-password/check-email',
    name: 'ForgotPasswordCheckEmail',
    meta: { title: 'Paymentshield | Forgot Password' },
    component: () => import('@/views/ForgotPassword/ForgotPasswordCheckEmailView.vue'),
  },
  {
    path: '/forgot-password/reset-password/',
    name: 'CreateNewPassword',
    meta: { title: 'Paymentshield | Forgot Password' },
    component: () => import('@/views/ForgotPassword/CreateNewPasswordView.vue'),
  },
  {
    path: '/account-setting',
    name: 'AccountSetting',
    meta: {
      requiresAuth: true,
      title: 'Paymentshield | Account Setting',
    },
    component: () => import('@/views/AccountSetting/AccountSettingView.vue'),
  },
  {
    path: '/account-setting/change-password',
    name: 'ChangePassword',
    meta: {
      requiresAuth: true,
      requiresPermission: [USER_TYPE.POLICYHOLDER],
      title: 'Paymentshield | Change Password',
    },
    component: () => import('@/views/AccountSetting/ChangePasswordView.vue'),
  },
  {
    path: '/account-setting/change-email',
    name: 'ChangeEmail',
    meta: {
      requiresAuth: true,
      title: 'Paymentshield | Change Email',
    },
    component: () => import('@/views/AccountSetting/ChangeEmailView.vue'),
  },
  {
    path: '/account-setting/change-email-summary',
    name: 'ChangeEmailSummary',
    meta: {
      title: 'Paymentshield | Change Email Summary',
    },
    component: () => import('@/views/AccountSetting/ChangeEmailSummaryView.vue'),
  },
  {
    path: '/account-setting/change-email-verify',
    name: 'ChangeEmailVerify',
    meta: {
      title: 'Paymentshield | Change Email Verify',
    },
    component: () => import('@/views/AccountSetting/ChangeEmailVerifyView.vue'),
  },
  {
    path: '/account-setting/change-name',
    name: 'ChangeName',
    meta: {
      requiresPermission: [USER_TYPE.POLICYHOLDER],
      title: 'Paymentshield | Change Name',
    },
    component: () => import('@/views/AccountSetting/ChangeNameView.vue'),
  },
  {
    path: '/add-policy',
    name: 'AddPolicy',
    meta: {
      requiresPermission: [USER_TYPE.POLICYHOLDER],
      requiresAuth: true,
      title: 'Paymentshield | Add Policy',
    },
    component: () => import('@/views/Policy/AddPolicyView.vue'),
  },
  {
    path: '/policy/:ref?',
    name: 'PolicyDetails',
    meta: { requiresAuth: true, title: 'Paymentshield | Policy Details' },
    component: () => import('@/views/Policy/PolicySummary/PolicySummaryView.vue'),
  },
  {
    path: '/error-404',
    name: 'PageNotFound',
    meta: { title: 'Paymentshield | Page Not Found' },
    component: () => import('@/views/Error/PageNotFoundView.vue'),
  },
  {
    path: '/payment-success',
    name: 'PaymentSuccess',
    meta: {
      requiresPermission: [USER_TYPE.POLICYHOLDER],
      title: 'Paymentshield | Payment Successfully',
    },
    component: () => import('@/views/Policy/Payment/PaymentSuccessView.vue'),
  },
  {
    path: '/contact-us',
    name: 'ContactUs',
    meta: { title: 'Paymentshield | Contact Us' },
    component: () => import('@/views/ContactUs/ContactUsView.vue'),
  },
  {
    path: '/policy/:ref/document',
    name: 'PolicyDocument',
    meta: { requiresAuth: true, title: 'Paymentshield | Policy Document' },
    component: () => import('@/views/Policy/Document/PolicyDocumentView.vue'),
  },
  {
    path: '/adviser/home',
    name: 'AdviserHome',
    meta: {
      requiresAuth: true,
      requiresPermission: [USER_TYPE.ADVISER],
      title: 'Paymentshield | Adviser Home',
    },
    component: () => import('@/views/Adviser/Home/AdviserHomeView.vue'),
  },
  {
    path: '/resend-email',
    name: 'ResendEmail',
    meta: {
      requiresPermission: [USER_TYPE.ADVISER],
      requiresAuth: true,
      title: 'Paymentshield | Resend Email',
    },
    component: () => import('@/views/Adviser/ResendEmailView.vue'),
  },
  {
    path: '/policy/:ref/edit-payment',
    name: 'EditPayment',
    meta: {
      requiresPermission: [USER_TYPE.POLICYHOLDER],
      requiresAuth: true,
      title: 'Paymentshield | Edit Payment',
    },
    component: () => import('@/views/Policy/Payment/EditPaymentDetails.vue'),
  },
  { path: '', redirect: '/login' },
  { path: '*', redirect: '/error-404' },
];

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(() => {});
};

router.beforeEach((to, from, next) => {
  adobeTargetTriggerView(to.path);
  document.title = to.meta.title;

  let token = authService.getToken();

  document.body.setAttribute('tabindex', '-1');
  document.body.focus();
  document.body.removeAttribute('tabindex');

  const rememberMeToken = authService.getRememberMeToken();

  if (!token && rememberMeToken) {
    store
      .dispatch(AUTHENTICATION_REFRESH_TOKEN)
      .then(
        () => {},
        () => {},
      )
      .finally(() => {
        redirectHandler(to, next);
      });
  } else {
    redirectHandler(to, next);
  }
});

const redirectHandler = (to, next) => {
  const token = authService.getToken();
  const { requiresAuth, requiresPermission } = to.meta;
  const userType = authService.getUserType();

  if (!isEmpty(token)) {
    if (indexOf(REDIRECT_TO_HOME_WITH_LOGGED_USER_PAGES, to.name) > -1) {
      const redirectUrl =
        userType === USER_TYPE.POLICYHOLDER ? HOME_PAGE_URL : ADVISER_HOME_PAGE_URL;
      next({ path: redirectUrl });
    } else if (requiresPermission && indexOf(requiresPermission, userType) < 0) {
      next({ path: PAGE_NOT_FOUND_PAGE_URL });
    } else {
      next();
    }
  } else {
    if (requiresAuth) {
      return next({ path: LOGIN_PAGE_URL });
    }
    next();
  }
};

export default router;
